import { css } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives/responsive';
import UFOLabel from '@atlaskit/react-ufo/label';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { useTheme } from '@/libraries/design/utils/dark-theme-provider';
import { LogoV3 } from '@/libraries/wpl/foundations/src/logo-v3';
import { LinkListContainer } from '@/patterns/global-footer/src/global-footer-top-link-list-container';

// The Foundation site is hosted separately from atlassian.com, so every link to it
// has to be absolute - the footer itself renders on atlassian.com Uniform pages.
const FOUNDATION_SITE = 'https://www.atlassianfoundation.org';

// ==================================================================
// CSS styling
// ==================================================================
const containerStyle = css({
	backgroundColor: wacToken('color.neutral.light100'),
	paddingBlock: '24px',
	paddingInline: token('space.200'),
	borderRadius: '0 0 24px 24px',
	width: '100%',
	maxWidth: '1216px',
	margin: 'auto',

	[media.only.sm]: {
		paddingInline: '44px',
	},

	[media.above.md]: {
		paddingInline: '24px',
	},
});

const containerDarkThemeStyle = css({
	backgroundColor: 'transparent',
});

const linksWrapperStyle = css({
	gridArea: 'links',
	display: 'grid',
	gridTemplateColumns: '1fr',
	gap: token('space.400'),

	[media.only.sm]: {
		gridTemplateColumns: 'repeat(2, minmax(0,1fr))',
		columnGap: '24px',
		rowGap: '48px',
	},

	[media.above.md]: {
		gridTemplateColumns: 'repeat(4, minmax(0,1fr))',
	},
});

const logoContainerStyle = css({
	height: '32px',
	marginBlockStart: token('space.0'),
	marginBlockEnd: token('space.400'),
	display: 'flex',
	alignItems: 'center',
});

const logoLinkStyle = css({
	display: 'inline-flex',
	alignItems: 'center',
	width: '100%',
	maxWidth: '280px',
	// The Foundation lockup is a wide wordmark (539x48), so let it shrink with the column.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& svg': {
		width: '100%',
		height: 'auto',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&:focus-visible': {
		outline: `2px solid ${wacToken('color.primary.blue')}`,
		outlineOffset: '2px',
	},
});

// ==================================================================
// Rendering Functions
// ==================================================================
const messages = defineMessages({
	ariaLabelLogo: {
		id: 'foundation.globalfooter.top.logo-label',
		defaultMessage: 'Atlassian Foundation logo',
	},
});

const FoundationLogoLink = () => {
	const { isDarkTheme } = useTheme();
	const intl = useIntl();

	return (
		<div css={logoContainerStyle}>
			<a
				aria-label={intl.formatMessage(messages.ariaLabelLogo)}
				href={`${FOUNDATION_SITE}/`}
				css={logoLinkStyle}
				data-event="clicked"
				data-event-component="linkButton"
				data-event-container="globalFooterFoundation"
			>
				<LogoV3
					brand="foundation"
					type="logo"
					appearance="brand"
					width={280}
					height={25}
					darkTheme={isDarkTheme}
				/>
			</a>
		</div>
	);
};

export const FoundationFooterTop = () => {
	const { isDarkTheme } = useTheme();
	const intl = useIntl();

	const aboutLinkList = {
		headerText: intl.formatMessage({
			id: 'foundation.globalfooter.top.about',
			defaultMessage: 'About',
		}),
		listOfLinks: [
			{
				text: intl.formatMessage({
					id: 'foundation.globalfooter.top.about-us',
					defaultMessage: 'About us',
				}),
				href: `${FOUNDATION_SITE}/about`,
			},
			{
				text: intl.formatMessage({
					id: 'foundation.globalfooter.top.our-policies',
					defaultMessage: 'Our policies',
				}),
				href: `${FOUNDATION_SITE}/policies`,
			},
			{
				text: intl.formatMessage({
					id: 'foundation.globalfooter.top.contact-us',
					defaultMessage: 'Contact us',
				}),
				href: 'https://atlassianfoundation.atlassian.net/servicedesk/customer/portals',
			},
		],
	};

	const focusAreasLinkList = {
		headerText: intl.formatMessage({
			id: 'foundation.globalfooter.top.focus-areas',
			defaultMessage: 'Focus areas',
		}),
		listOfLinks: [
			{
				text: intl.formatMessage({
					id: 'foundation.globalfooter.top.philanthropy',
					defaultMessage: 'Philanthropy',
				}),
				href: `${FOUNDATION_SITE}/how-we-work/education`,
			},
			{
				text: intl.formatMessage({
					id: 'foundation.globalfooter.top.atlassian-for-nonprofits',
					defaultMessage: 'Atlassian for Nonprofits',
				}),
				href: 'https://www.atlassian.com/teams/nonprofits',
			},
			{
				text: intl.formatMessage({
					id: 'foundation.globalfooter.top.volunteering-and-donating',
					defaultMessage: 'Volunteering & donating',
				}),
				href: `${FOUNDATION_SITE}/how-we-work/volunteering-and-donating`,
			},
		],
	};

	const partnersLinkList = {
		headerText: intl.formatMessage({
			id: 'foundation.globalfooter.top.partners',
			defaultMessage: 'Partners',
		}),
		listOfLinks: [
			{
				text: intl.formatMessage({
					id: 'foundation.globalfooter.top.education-partners',
					defaultMessage: 'Education partners',
				}),
				href: `${FOUNDATION_SITE}/Education-partners`,
			},
			{
				text: intl.formatMessage({
					id: 'foundation.globalfooter.top.pledge',
					defaultMessage: 'Pledge 1%',
				}),
				href: `${FOUNDATION_SITE}/how-we-work/pledge`,
			},
			{
				text: intl.formatMessage({
					id: 'foundation.globalfooter.top.crisis-response-partners',
					defaultMessage: 'Crisis response partners',
				}),
				href: `${FOUNDATION_SITE}/Education-partners/crisis-response-partners`,
			},
			{
				text: intl.formatMessage({
					id: 'foundation.globalfooter.top.impact-investing',
					defaultMessage: 'Impact investing',
				}),
				href: `${FOUNDATION_SITE}/how-we-work/impact-investing`,
			},
		],
	};

	return (
		<UFOLabel name="global-footer-foundation-top">
			<div css={[containerStyle, isDarkTheme && containerDarkThemeStyle]}>
				<div css={linksWrapperStyle}>
					<FoundationLogoLink />
					<LinkListContainer {...aboutLinkList} />
					<LinkListContainer {...focusAreasLinkList} />
					<LinkListContainer {...partnersLinkList} />
				</div>
			</div>
		</UFOLabel>
	);
};
